<template>
  <section
    ref="sectionEl"
    class="py-28 bg-white dark:bg-dark relative overflow-hidden transition-colors duration-300"
    :class="{ 'is-visible': isVisible }"
  >
    <div class="section-glow-left"></div>

    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-3xl mx-auto text-center mb-20 anim-el d1">
        <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">Analisi</p>
        <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-6 transition-colors duration-300">
          {{ t('problemSolution.title') }}
        </h2>
        <p class="text-lg text-gray-500 dark:text-gray-400 leading-relaxed">
          {{ t('problemSolution.subtitle') }}
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
        <!-- Problems column -->
        <div>
          <div class="flex items-center gap-3 mb-6">
            <div class="h-px flex-1 bg-red-200 dark:bg-red-500/20"></div>
            <span class="text-[13px] uppercase tracking-[0.15em] text-red-500 dark:text-red-400 font-semibold">{{ t('problemSolution.problems.title') }}</span>
            <div class="h-px flex-1 bg-red-200 dark:bg-red-500/20"></div>
          </div>

          <div class="space-y-3">
            <div
              v-for="(problem, index) in problems"
              :key="index"
              class="flex items-start gap-4 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] p-5 hover:border-red-200 dark:hover:border-red-500/20 transition-all duration-300 group anim-el"
              :class="'d' + (index + 2)"
            >
              <div class="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-105 transition-transform duration-300">
                <component :is="problem.icon" class="h-4 w-4 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <h4 class="text-[15px] font-semibold text-gray-900 dark:text-white mb-1 transition-colors duration-300">{{ problem.title }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ problem.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Solutions column -->
        <div>
          <div class="flex items-center gap-3 mb-6">
            <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
            <span class="text-[13px] uppercase tracking-[0.15em] text-blue-500 dark:text-blue-400 font-semibold">{{ t('problemSolution.solutions.title') }}</span>
            <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
          </div>

          <div class="space-y-3">
            <div
              v-for="(solution, index) in solutions"
              :key="index"
              class="flex items-start gap-4 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] p-5 hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300 group anim-el"
              :class="'d' + (index + 2)"
            >
              <div class="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-105 transition-transform duration-300">
                <component :is="solution.icon" class="h-4 w-4 text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <h4 class="text-[15px] font-semibold text-gray-900 dark:text-white mb-1 transition-colors duration-300">{{ solution.title }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ solution.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="text-center anim-el d8">
        <p class="text-gray-500 dark:text-gray-400 mb-6 max-w-2xl mx-auto">{{ t('problemSolution.cta.description') }}</p>
        <router-link
          to="/contact"
          class="inline-flex items-center h-12 px-7 bg-gray-900 dark:bg-white text-white dark:text-dark font-semibold text-[15px] rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors group"
        >
          {{ t('problemSolution.cta.button') }}
          <ArrowRight class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </router-link>
      </div>
    </div>
  </section>
</template>

<script setup>
import { Clock, DollarSign, AlertTriangle, Bot, Rocket, Shield, TrendingUp, ArrowRight } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { computed, ref, onMounted, onUnmounted } from 'vue'

const { t } = useI18n()

const sectionEl = ref(null)
const isVisible = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) isVisible.value = true
    })
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' })
  if (sectionEl.value) observer.observe(sectionEl.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const problems = computed(() => [
  { icon: Clock, title: t('problemSolution.problems.time.title'), description: t('problemSolution.problems.time.description') },
  { icon: DollarSign, title: t('problemSolution.problems.cost.title'), description: t('problemSolution.problems.cost.description') },
  { icon: AlertTriangle, title: t('problemSolution.problems.errors.title'), description: t('problemSolution.problems.errors.description') },
  { icon: Shield, title: t('problemSolution.problems.scalability.title'), description: t('problemSolution.problems.scalability.description') }
])

const solutions = computed(() => [
  { icon: Bot, title: t('problemSolution.solutions.automation.title'), description: t('problemSolution.solutions.automation.description') },
  { icon: TrendingUp, title: t('problemSolution.solutions.efficiency.title'), description: t('problemSolution.solutions.efficiency.description') },
  { icon: Shield, title: t('problemSolution.solutions.accuracy.title'), description: t('problemSolution.solutions.accuracy.description') },
  { icon: Rocket, title: t('problemSolution.solutions.growth.title'), description: t('problemSolution.solutions.growth.description') }
])
</script>

<style scoped>
.section-glow-left {
  position: absolute;
  top: 20%;
  left: -10%;
  width: 35%;
  height: 50%;
  background: radial-gradient(ellipse at center, rgba(59, 130, 246, 0.05) 0%, transparent 60%);
  pointer-events: none;
}

.anim-el {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s cubic-bezier(.22,1,.36,1), transform 0.6s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.10s; }
.d3 { transition-delay: 0.17s; }
.d4 { transition-delay: 0.24s; }
.d5 { transition-delay: 0.31s; }
.d6 { transition-delay: 0.38s; }
.d7 { transition-delay: 0.45s; }
.d8 { transition-delay: 0.52s; }
</style>
